import * as React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import { useToastError } from '@hooks';
import { Spot, useUpdateSpotCapacityMutation } from '@graphql';

const STEP = 10;
const MIN = 10;
const MAX = 1000;

interface CapacityProps {
  handleSwiperTouchMove: (isActive: boolean) => void;
  account: Spot;
}

export default function Capacity(props: CapacityProps) {
  const { handleSwiperTouchMove, account } = props;
  const [isEditing, setIsEditing] = React.useState(false);
  const toastError = useToastError();
  const { t } = useTranslation('cta');

  const [updateCapacity] = useUpdateSpotCapacityMutation();

  const initialValues = { capacity: account.configuration.capacity || 0 };
  const validationSchema = yup.object({
    capacity: yup.number().min(10).max(1000).required(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      updateCapacity({
        variables: { value: values.capacity },
        onCompleted({ updateSpotCapacity }) {
          formik.resetForm({ values });
          toast(t('spotCapacity.toast.success', { ns: 'field' }), { type: 'success' });
        },
        onError: toastError,
      });
    },
  });

  const handleEdit = () => {
    handleSwiperTouchMove(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    formik.resetForm({ values: initialValues });
    setIsEditing(false);
    handleSwiperTouchMove(true);
  };

  const handleSetVal = (capacity: number) => {
    formik.setFieldValue('capacity', capacity);
    formik.setFieldTouched('capacity', (account.configuration.capacity as number) !== capacity);
  };

  const handleValidate = () => {
    formik.submitForm();
    setIsEditing(false);
    handleSwiperTouchMove(false);
  };

  return (
    <Stack width="100%" spacing={1}>
      <Stack direction="row" width="100%" justifyContent="space-between">
        <Stack>
          <Typography fontWeight="bold">{t('spotCapacity.label', { ns: 'field' })}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('spotCapacity.caption', { ns: 'field' })}
          </Typography>
        </Stack>
        <Stack spacing={1} direction="row" alignItems="flex-start">
          {isEditing ? (
            <>
              <Button variant="contained" color="uncolored" size="small" onClick={handleCancel}>
                {t('cancel')}
              </Button>
              <Button
                disabled={!formik.dirty || !formik.isValid}
                onClick={handleValidate}
                variant="contained"
                color="success"
                size="small"
                type="submit"
              >
                {t('validate')}
              </Button>
            </>
          ) : (
            <Button variant="contained" color="uncolored" size="small" onClick={handleEdit}>
              {t('edit')}
            </Button>
          )}
        </Stack>
      </Stack>
      <Stack>
        <Slider
          sx={{ mt: 5 }}
          name="capacity"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.capacity}
          defaultValue={formik.values.capacity}
          valueLabelDisplay="on"
          step={STEP}
          min={MIN}
          max={MAX}
          disabled={!isEditing}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" onClick={() => handleSetVal(MIN)} sx={{ cursor: 'pointer' }}>
            {MIN} pers
          </Typography>
          <Typography variant="body2" onClick={() => handleSetVal(MAX)} sx={{ cursor: 'pointer' }}>
            +{MAX} pers
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}
